import dayjs from 'dayjs'
import { FC, useState } from 'react'

import { cn } from '@/app/utils'
import { Calendar } from '@/components/shared/calendar'

import { AppButton } from '../../button'
import { CalendarCaption } from './caption/CalendarCaption'
import { EmptyChevron } from './EmptyChevron'

type Props = {
  date?: Date
  fromMonth?: Date
  today?: Date
  disabled?: {
    before?: Date
    after?: Date
  }
  onChange?: (date: Date) => void
  close?: () => void
}

const getDatePickerComponents = (
  month: Date,
  setMonth: (month: Date) => void,
  fromMonth?: Date
) => ({
  MonthCaption: () => (
    <CalendarCaption
      month={month}
      setMonth={setMonth}
      fromMonth={fromMonth}
      isPrevMonthDisabled={dayjs(fromMonth).isSame(month, 'month')}
    />
  ),
  Chevron: EmptyChevron
})

const DatePicker: FC<Props> = ({
  date,
  onChange,
  close,
  fromMonth,
  today,
  disabled
}) => {
  const referenceToday = today ?? dayjs().startOf('day').toDate()

  const resolvedDisabled =
    disabled?.before || disabled?.after
      ? [
          ...(disabled.before ? [{ before: disabled.before }] : []),
          ...(disabled.after ? [{ after: disabled.after }] : [])
        ]
      : { before: referenceToday }
  const [localDate, setLocalDate] = useState<Date | undefined>(
    date || referenceToday
  )
  const [month, setMonth] = useState(date ?? referenceToday)

  const handleApply = () => {
    if (localDate) {
      onChange?.(localDate)
    }
    close?.()
  }

  const onSelectDate = (selectedDate?: Date) => {
    if (selectedDate === undefined) {
      return
    }
    setLocalDate(selectedDate)
  }

  return (
    <div
      className={cn(
        'absolute z-[60] mt-8 flex w-full flex-col items-center justify-center gap-8 rounded-12 border border-grey-20 bg-white',
        'xxs:relative xxs:h-full xxs:justify-between xxs:border-0'
      )}
    >
      <div className="flex flex-col justify-between p-12 xxs:h-full xxs:p-0">
        <Calendar
          classNames={{
            head_cell: 'w-36 h-34 xxs:w-49',
            cell: 'w-36 h-34 xxs:w-49'
          }}
          mode="single"
          selected={localDate}
          components={getDatePickerComponents(month, setMonth, fromMonth)}
          onSelect={onSelectDate}
          defaultMonth={referenceToday}
          onMonthChange={setMonth}
          month={month}
          today={today}
          disabled={resolvedDisabled}
        />
        <AppButton
          className="mt-16 w-full"
          variant="secondary"
          onClick={handleApply}
        >
          Выбрать
        </AppButton>
      </div>
    </div>
  )
}

export default DatePicker
